import { Show, createEffect, on } from "solid-js";
import { A } from "@solidjs/router";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Band } from "../../components/Band.tsx";
import { CoverageBar } from "../../components/CoverageBar.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { formatMoment, formatScore } from "../../lib/format.ts";
import { questionnaireStatus } from "../../api/domain.ts";
import { ok, type Result } from "../../api/result.ts";
import type { Questionnaire, Report, ReportSnapshot } from "../../api/domain.ts";
import { statusLabel } from "./questionnaire-status.ts";
import {
  historyTargets,
  newestReport,
  productSummaries,
  recentEvents,
  type HistoryTarget,
  type LabelledEvent,
  type ScoreOrigin,
} from "./dashboard.ts";
import { describeChange, historyHref, asOfHref } from "./history.ts";
import styles from "./admin.module.scss";

/** An evaluation to read scores from, where it came from, and its siblings. */
type Scores = {
  origin: ScoreOrigin;
  reports: Report[];
  snapshot?: ReportSnapshot;
};

/**
 * Products with their latest score and coverage, questionnaire statuses and the
 * newest change events. The scores come from the newest report, preferring a
 * locked one, because that is the number the organisation has agreed on.
 */
export const DashboardPage = () => {
  const api = useApi();
  const i18n = useI18n();

  const products = createLoader(() => api.list("products", { lang: i18n.locale() }));
  const questionnaires = createLoader(() => api.list("questionnaires"));
  const scores = createLoader<Scores>(async () => {
    const listed = await api.list("reports");
    if (!listed.ok) return listed;

    const newest = newestReport(listed.value);
    if (newest === undefined) return ok({ origin: "none" as const, reports: listed.value });

    const evaluation = await api.reportEvaluation(newest.id);
    if (!evaluation.ok) return evaluation;
    return ok({
      origin: newest.locked_at === null ? ("live" as const) : ("locked" as const),
      reports: listed.value,
      snapshot: evaluation.value,
    });
  });

  const scoreNote = (origin: ScoreOrigin): string => {
    if (origin === "locked") return i18n.t().dashboard.scoresFromLocked;
    if (origin === "live") return i18n.t().dashboard.scoresFromLive;
    return i18n.t().dashboard.noEvaluation;
  };

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().dashboard.title}</h1>
      </div>

      <Async value={scores.value()} onRetry={scores.reload}>
        {(found) => (
          <Panel title={i18n.t().dashboard.scores} description={scoreNote(found.origin)}>
            <Async value={products.value()} onRetry={products.reload}>
              {(rows) => (
                <Table
                  rows={productSummaries(rows, found.snapshot)}
                  rowKey={(summary) => summary.product}
                  emptyLabel={i18n.t().common.empty}
                  columns={[
                    {
                      key: "name",
                      header: i18n.t().common.name,
                      cell: (summary) => (
                        <A href={`/products/${summary.product}`}>{summary.name}</A>
                      ),
                    },
                    {
                      key: "score",
                      header: i18n.t().common.score,
                      align: "end",
                      cell: (summary) =>
                        summary.score === null
                          ? i18n.t().common.notAssessed
                          : formatScore(summary.score, i18n.locale()),
                    },
                    {
                      key: "band",
                      header: i18n.t().common.band,
                      cell: (summary) => <Band score={summary.score} />,
                    },
                    {
                      key: "coverage",
                      header: i18n.t().common.coverage,
                      cell: (summary) => (
                        <Show
                          when={summary.coverage}
                          fallback={<span class={styles.note}>{i18n.t().common.notAvailable}</span>}
                        >
                          {(coverage) => <CoverageBar {...coverage()} />}
                        </Show>
                      ),
                    },
                    {
                      key: "history",
                      header: i18n.t().common.actions,
                      cell: (summary) => (
                        <A href={historyHref("product", summary.product)}>
                          {i18n.t().history.link}
                        </A>
                      ),
                    },
                  ]}
                />
              )}
            </Async>
          </Panel>
        )}
      </Async>

      <Panel title={i18n.t().dashboard.questionnaires}>
        <Async value={questionnaires.value()} onRetry={questionnaires.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(questionnaire: Questionnaire) => questionnaire.id}
              emptyLabel={i18n.t().common.empty}
              columns={[
                {
                  key: "id",
                  header: i18n.t().common.identity,
                  cell: (questionnaire) => (
                    <A href={`/questionnaires/${questionnaire.id}`}>{questionnaire.id}</A>
                  ),
                },
                {
                  key: "status",
                  header: i18n.t().common.status,
                  cell: (questionnaire) =>
                    statusLabel(i18n.t(), questionnaireStatus(questionnaire)),
                },
                {
                  key: "created",
                  header: i18n.t().questionnaires.created,
                  cell: (questionnaire) => formatMoment(questionnaire.created_at, i18n.locale()),
                },
                {
                  key: "history",
                  header: i18n.t().common.actions,
                  cell: (questionnaire) => (
                    <A href={historyHref("questionnaire", questionnaire.id)}>
                      {i18n.t().history.link}
                    </A>
                  ),
                },
              ]}
            />
          )}
        </Async>
      </Panel>

      <Panel title={i18n.t().dashboard.changes}>
        <ChangeLog
          targets={historyTargets(
            loadedValue(products.value()) ?? [],
            loadedValue(questionnaires.value()) ?? [],
            loadedValue(scores.value())?.reports ?? [],
          )}
        />
      </Panel>
    </>
  );
};

type ChangeLogProps = {
  targets: HistoryTarget[];
};

/**
 * The newest change events of the entities the dashboard already knows about.
 * The target list is bounded, so the number of history requests is too.
 */
export const ChangeLog = (props: ChangeLogProps) => {
  const api = useApi();
  const i18n = useI18n();

  const events = createLoader<LabelledEvent[]>(async (): Promise<Result<LabelledEvent[]>> => {
    const collected: LabelledEvent[] = [];
    for (const target of props.targets) {
      const result = await api.history(target.entityType, target.id, { limit: 5 });
      if (!result.ok) return result;
      collected.push(...result.value.map((event) => ({ ...event, label: target.label })));
    }
    return ok(recentEvents(collected));
  });

  createEffect(
    on(
      () => props.targets.map((target) => `${target.entityType}:${target.id}`).join(","),
      () => events.reload(),
      { defer: true },
    ),
  );

  return (
    <Async value={events.value()} onRetry={events.reload}>
      {(rows) => (
        <Table
          rows={rows}
          rowKey={(event) => event.id}
          emptyLabel={i18n.t().history.noEvents}
          columns={[
            {
              key: "entity",
              header: i18n.t().dashboard.entity,
              cell: (event) => (
                <A href={historyHref(event.entity_type, event.entity_id)}>{event.label}</A>
              ),
            },
            {
              key: "change",
              header: i18n.t().history.change,
              cell: (event) => describeChange(i18n.t(), event),
            },
            { key: "actor", header: i18n.t().dashboard.actor, cell: (event) => event.actor },
            {
              key: "at",
              header: i18n.t().dashboard.at,
              cell: (event) => formatMoment(event.at, i18n.locale()),
            },
            {
              key: "asOf",
              header: i18n.t().common.actions,
              cell: (event) => <A href={asOfHref(event.at)}>{i18n.t().history.asOfJump}</A>,
            },
          ]}
        />
      )}
    </Async>
  );
};
